import { makeAutoObservable, runInAction } from 'mobx';

export interface UpdateEditor {
  context?: unknown;
  uiModel?: { inDialog: boolean };
  hasPendingProjectEdits?: boolean;
  hasUnsavedProjectChanges: boolean;
  hasUnsavedChipDrafts: boolean;
  projectSaveBlockedReason?: string | null;
  saveCurrentProject(): Promise<unknown>;
}

/** Save and recheck at both asynchronous boundaries; never discard editor work. */
export class UpdateModel {
  active = true;
  busy = false;
  error = '';
  dismissed = false;
  constructor(
    private editor: UpdateEditor,
    private activate: (canReload: () => boolean) => Promise<void>,
  ) {
    makeAutoObservable<this, 'editor' | 'activate'>(
      this,
      { editor: false, activate: false },
      { autoBind: true },
    );
  }
  setActive(active: boolean) {
    this.active = active;
  }
  dismiss() {
    this.dismissed = true;
  }
  async update() {
    if (this.busy) return;
    this.busy = true;
    this.error = '';
    const context = this.editor.context;
    try {
      if (this.editor.uiModel?.inDialog) throw Error('Close the dialog before updating.');
      if (this.editor.projectSaveBlockedReason)
        throw Error(this.editor.projectSaveBlockedReason);
      if (this.editor.hasUnsavedChipDrafts)
        throw Error('Save or discard chip drafts first.');
      if (this.editor.context || this.editor.hasUnsavedProjectChanges)
        await this.editor.saveCurrentProject();
      if (this.editor.hasUnsavedProjectChanges || this.editor.hasPendingProjectEdits)
        throw Error('New changes arrived. Save and try again.');
      if (!this.active || this.editor.context !== context) return;
      await this.activate(
        () =>
          this.active &&
          this.editor.context === context &&
          !this.editor.uiModel?.inDialog &&
          !this.editor.hasUnsavedProjectChanges &&
          !this.editor.hasPendingProjectEdits &&
          !this.editor.projectSaveBlockedReason,
      );
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : 'Update unavailable. Try again.';
      });
    } finally {
      runInAction(() => {
        this.busy = false;
      });
    }
  }
}
